"use client";
import { useTranslations } from "@zapier/i18n";

import { useState, ChangeEvent } from "react";
import { Icon } from "@zapier/zinnia-icons";

import {
  Alert,
  Button,
  Field,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  Text,
  TextInput,
  ToggleSwitch,
} from "@zapier/design-system";

import {
  getMessageFromNetworkError,
  useRequestApproval,
  useUserAccountRole,
} from "@zapier/client";
import { ApprovalType, isUntitledZap, useNotification } from "@zapier/utils";

import styles from "./RequestApprovalModal.module.css";

interface Props {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  title?: string;
  type: ApprovalType | undefined;
  zapId?: string;
}

interface ContentProps extends Props {
  message: string;
  onSubmit: () => void;
  setMessage: (message: string) => void;
}

function RequestApprovalContent({
  isOpen,
  message,
  onSubmit,
  setIsOpen,
  setMessage,
  title,
  type,
}: ContentProps) {
  const t = useTranslations("request-approval-modal");

  const characterCount = `${message.length}/500 characters`;

  const onChangeMessage = (event: ChangeEvent<HTMLInputElement>) => {
    setMessage(event.target.value);
  };

  const zapTitle = title || t("approval-type.untitled-zap");
  const isZapUntitled = isUntitledZap(title);

  return (
    <ModalDialogRoot
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      className={styles.modalContent}
    >
      <ModalDialogHeader textAlign="start">
        <Text color="GrayWarm10" type="Body4">
          {t("header.title")}
        </Text>
      </ModalDialogHeader>
      <ModalDialogBody textAlign="start" className={styles.body}>
        <Text color="GrayWarm10" type="Body1">
          {t("content.description")}
        </Text>
        <div className={styles.approvalType}>
          {type === ApprovalType.Delete ? (
            <span className={styles.toggleSwitch}>
              <Icon
                name="actionTrash"
                size="40px"
                color="StatusErrorStronger"
                aria-label={t("accessibility.delete-aria-label")}
              />
            </span>
          ) : (
            <span className={styles.toggleSwitch}>
              <ToggleSwitch
                checked={type === ApprovalType.TurnOn}
                aria-label={
                  type === ApprovalType.TurnOn
                    ? t("accessibility.turn-on-aria-label")
                    : t("accessibility.turn-off-aria-label")
                }
              />
            </span>
          )}
          <Text color="GrayWarm10" type="Body3">
            {type === ApprovalType.Delete
              ? t("approval-type.delete", { zapTitle })
              : type === ApprovalType.TurnOn
                ? t("approval-type.turn-on", { zapTitle })
                : t("approval-type.turn-off", { zapTitle })}
          </Text>
        </div>
        <Field
          isRequired={type === ApprovalType.Delete}
          label={t("form.message-field-label")}
          renderInput={(inputProps) => (
            <TextInput
              {...inputProps}
              isMultiline
              rows={2}
              maxLength={500}
              value={message}
              layout="block"
              appendContent={
                <div className={styles.characterCountWrapper}>
                  <div className={styles.characterCount}>{characterCount}</div>
                </div>
              }
              onChange={onChangeMessage}
            />
          )}
        />
        <Text color="GrayWarm10" type="SmallPrint1">
          {t("form.message-field-help-text")}
        </Text>
        {isZapUntitled && (
          <div className={styles.warningAlertContainer}>
            <Alert
              variant="default"
              title={t("warnings.zap-title-required")}
              status="warning"
            >
              {t("warnings.zap-title-required-description")}
            </Alert>
          </div>
        )}
      </ModalDialogBody>
      <ModalDialogFooter>
        <Button variant="secondary" onPress={() => setIsOpen(false)}>
          {t("buttons.cancel")}
        </Button>

        <Button
          onPress={onSubmit}
          isDisabled={
            isZapUntitled ||
            (type === ApprovalType.Delete && message.length === 0)
          }
        >
          {t("buttons.send-request")}
        </Button>
      </ModalDialogFooter>
    </ModalDialogRoot>
  );
}

export const RequestApprovalModal = ({
  isOpen,
  zapId,
  setIsOpen,
  type,
  title,
}: Props) => {
  const t = useTranslations("request-approval-modal");

  const { createNotification } = useNotification();
  const { requestApproval } = useRequestApproval();
  const { customuserId } = useUserAccountRole();
  const [message, setMessage] = useState("");

  // This callback needs to happen outside of the modal so that it can
  // still display a notification when the request completes after the modal has closed.
  const handleSubmit = async () => {
    if (!zapId) {
      return;
    }

    await requestApproval(
      {
        requestDescription: message,
        type,
        userId: String(customuserId),
        zapId,
      },
      {
        onSuccess: () => {
          createNotification({
            message: <Text>{t("notifications.success-message")}</Text>,
            type: "success",
          });
        },
        onError: (error: any) => {
          createNotification({
            title: t("notifications.error-title"),
            message: getMessageFromNetworkError(error),
            type: "error",
          });
        },
      },
    );

    setMessage("");
    setIsOpen(false);
  };

  // Do not display the modal if isOpen==false
  if (!isOpen) {
    return;
  }

  return (
    <RequestApprovalContent
      isOpen={isOpen}
      message={message}
      onSubmit={handleSubmit}
      setIsOpen={setIsOpen}
      setMessage={setMessage}
      title={title}
      type={type}
      zapId={zapId}
    />
  );
};
